import React, { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const APP_TYPES = ["CRM", "ERP", "Ecommerce", "Booking", "Dashboard", "Billing Panel", "SaaS"];

export default function AIAppBuilder() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ app_type: "CRM", name: "", description: "", key_features: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const go = async () => {
    if (!user) { toast.error("Please login first"); nav("/login?next=/ai-app-builder"); return; }
    setLoading(true);
    try {
      const payload = { ...form, key_features: form.key_features.split(",").map(s=>s.trim()).filter(Boolean) };
      const { data } = await api.post("/ai/app-builder", payload);
      setResult(data.output);
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="section max-w-5xl">
      <div className="text-center mb-8">
        <div className="tag mb-3">AI App Builder</div>
        <h1 className="text-5xl font-black tracking-tighter" style={{fontFamily:'Outfit'}}>Generate a <span className="text-gradient-cyan">SaaS app</span> blueprint.</h1>
        <p className="text-gray-400 mt-3">Entity models, API routes, UI screens — fully scoped, instantly.</p>
      </div>

      <div className="card p-6 sm:p-8 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label">App Type</label>
            <select data-testid="app-type" value={form.app_type} onChange={set("app_type")} className="input w-full">
              {APP_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="label">App Name</label>
            <input data-testid="app-name" value={form.name} onChange={set("name")} className="input w-full" placeholder="HelixCRM" />
          </div>
        </div>
        <div>
          <label className="label">Description</label>
          <textarea data-testid="app-description" value={form.description} onChange={set("description")} rows={3} className="input w-full" placeholder="A CRM for B2B SaaS founders to track leads and run drip campaigns..." />
        </div>
        <div>
          <label className="label">Key Features (comma-separated)</label>
          <input data-testid="app-features" value={form.key_features} onChange={set("key_features")} className="input w-full" placeholder="Lead capture, Pipeline, Email automation, Reports" />
        </div>
        <div className="flex justify-end">
          <button onClick={go} disabled={loading} className="btn-primary inline-flex items-center gap-2" data-testid="app-generate">
            {loading ? <Loader2 size={16} className="animate-spin"/> : <Sparkles size={16}/>} Generate Blueprint
          </button>
        </div>
      </div>

      {result && (
        <div className="card p-6 sm:p-8 mt-6 space-y-5" data-testid="app-result">
          {result.summary && <p className="text-gray-200">{result.summary}</p>}
          {result.tech_stack && (
            <div>
              <div className="text-cyan-400 text-xs uppercase tracking-wider mb-1">Tech Stack</div>
              <div className="text-gray-300 text-sm">FE: {result.tech_stack.frontend} · BE: {result.tech_stack.backend} · DB: {result.tech_stack.database}</div>
            </div>
          )}
          {result.entities && (
            <div>
              <div className="text-cyan-400 text-xs uppercase tracking-wider mb-2">Entities</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {result.entities.map((e,i)=>(
                  <div key={i} className="border border-white/10 rounded-lg p-3 bg-white/[0.02]">
                    <div className="font-semibold text-white">{e.name}</div>
                    <ul className="text-xs text-gray-400 mt-1 space-y-0.5">
                      {(e.fields||[]).map((f,j)=><li key={j}>{f.name}: <span className="text-cyan-300">{f.type}</span></li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.api_routes && (
            <div>
              <div className="text-cyan-400 text-xs uppercase tracking-wider mb-2">API Routes</div>
              <div className="space-y-1.5">
                {result.api_routes.map((r,i)=>(
                  <div key={i} className="font-mono text-xs flex gap-3">
                    <span className="text-cyan-400 font-bold w-14">{r.method}</span>
                    <span className="text-gray-200">{r.path}</span>
                    <span className="text-gray-500">— {r.purpose}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.milestones && (
            <div>
              <div className="text-cyan-400 text-xs uppercase tracking-wider mb-2">Milestones</div>
              <ol className="text-sm text-gray-300 list-decimal pl-5 space-y-1">{result.milestones.map((m,i)=><li key={i}>{m}</li>)}</ol>
            </div>
          )}
          <div className="pt-4 border-t border-white/10">
            <button onClick={()=>nav("/pricing")} className="btn-primary">Order this app build</button>
          </div>
        </div>
      )}
    </div>
  );
}
